import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAllTeacherCohortEvents,
  getTeacherCohortEventLog,
  type CohortEventRecord,
  type TeacherCohortRecord,
} from "../auth/api-client";
import { isLiveWsConfigured } from "../auth-config";
import { parseApiInstant } from "../datetime/parseApiInstant";
import {
  cohortEventIconSrc,
  eventMatchesActivityFilters,
  EVENT_TYPE_FILTER_OPTIONS,
  formatCohortEventMessage,
  studentOptionLabel,
} from "./cohort-event-icons";
import { exportCohortActivityCsv } from "./exportCohortActivityCsv";
import { useTeacherLiveActivity } from "./useTeacherLiveActivity";

export function CohortEventLogSection({
  bearer,
  cohorts,
  cohortId: scopedCohortId = null,
  lockCohortFilter = false,
}: {
  readonly bearer: string | null;
  readonly cohorts: TeacherCohortRecord[];
  /** When set with lockCohortFilter, follows the parent cohort scope (null = all cohorts). */
  readonly cohortId?: string | null;
  readonly lockCohortFilter?: boolean;
}) {
  const [filterCohortId, setFilterCohortId] = useState("");
  const [filterEventType, setFilterEventType] = useState("");
  const [filterStudentUserId, setFilterStudentUserId] = useState("");
  const [filterLessonId, setFilterLessonId] = useState("");
  const [events, setEvents] = useState<CohortEventRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const pageSize = 40;
  const activeCohortId = lockCohortFilter ? (scopedCohortId ?? "") : filterCohortId;
  const showCohortInMessage = !activeCohortId;

  const activityFilters = useMemo(
    () => ({
      cohortId: activeCohortId,
      eventType: filterEventType,
      studentUserId: filterStudentUserId,
      lessonId: filterLessonId,
    }),
    [activeCohortId, filterEventType, filterStudentUserId, filterLessonId]
  );

  const hasClientFilters = Boolean(filterEventType || filterStudentUserId || filterLessonId);

  const sortedCohorts = useMemo(
    () => [...cohorts].sort((a, b) => a.label.localeCompare(b.label)),
    [cohorts]
  );

  const studentOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const event of events) {
      if (!event.studentUserId) continue;
      if (!byId.has(event.studentUserId)) {
        byId.set(event.studentUserId, studentOptionLabel(event.studentEmail));
      }
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [events]);

  const lessonOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const event of events) {
      if (!event.lessonId) continue;
      if (!byId.has(event.lessonId)) {
        byId.set(event.lessonId, event.lessonTitle ?? "Untitled lesson");
      }
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [events]);

  const displayedEvents = useMemo(
    () => events.filter((event) => eventMatchesActivityFilters(event, activityFilters)),
    [events, activityFilters]
  );

  const load = useCallback(
    async (opts: { append: boolean; nextOffset: number }) => {
      if (!bearer) return;
      const { append, nextOffset } = opts;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const data = await getTeacherCohortEventLog(bearer, {
          cohortId: activeCohortId || undefined,
          limit: pageSize,
          offset: nextOffset,
        });
        setHasMore(data.events.length === pageSize);
        setOffset(nextOffset + data.events.length);
        setEvents((prev) => (append ? [...prev, ...data.events] : data.events));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load activity");
        if (!append) setEvents([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [bearer, activeCohortId, pageSize]
  );

  useEffect(() => {
    if (!bearer) return;
    setOffset(0);
    void load({ append: false, nextOffset: 0 });
  }, [bearer, activeCohortId, load]);

  useEffect(() => {
    setFilterEventType("");
    setFilterStudentUserId("");
    setFilterLessonId("");
  }, [activeCohortId]);

  useEffect(() => {
    if (filterStudentUserId && !studentOptions.some((s) => s.id === filterStudentUserId)) {
      setFilterStudentUserId("");
    }
  }, [filterStudentUserId, studentOptions]);

  useEffect(() => {
    if (filterLessonId && !lessonOptions.some((l) => l.id === filterLessonId)) {
      setFilterLessonId("");
    }
  }, [filterLessonId, lessonOptions]);

  const prependLiveEvent = useCallback(
    (event: CohortEventRecord) => {
      if (activeCohortId && event.cohortId !== activeCohortId) return;
      setEvents((prev) => {
        if (prev.some((e) => e.id === event.id)) return prev;
        return [event, ...prev];
      });
    },
    [activeCohortId]
  );

  const liveStatus = useTeacherLiveActivity({
    bearer,
    cohortId: activeCohortId,
    onEvent: prependLiveEvent,
  });

  const liveLabel =
    liveStatus === "live"
      ? "Live"
      : liveStatus === "connecting"
        ? "Connecting…"
        : liveStatus === "unavailable"
          ? "Live updates unavailable"
          : null;

  function clearClientFilters() {
    setFilterEventType("");
    setFilterStudentUserId("");
    setFilterLessonId("");
  }

  async function handleExport() {
    if (!bearer || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const all = await fetchAllTeacherCohortEvents(bearer, {
        cohortId: activeCohortId || undefined,
      });
      const filtered = all.filter((event) => eventMatchesActivityFilters(event, activityFilters));
      if (filtered.length === 0) {
        setError("No activity matches the current filters to export.");
        return;
      }
      const cohortLabel = activeCohortId
        ? sortedCohorts.find((c) => c.id === activeCohortId)?.label
        : null;
      const stem = cohortLabel
        ? `activity-${cohortLabel.replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "cohort"}`
        : "activity-all-cohorts";
      exportCohortActivityCsv({
        events: filtered,
        showCohortInMessage,
        filenameStem: stem,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not export activity");
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className="teacher-panel teacher-panel--flush cohort-event-log-section">
      <div className="teacher-panel-head-row teacher-pad">
        <div className="teacher-panel-head-copy">
          <h2 className="teacher-panel-title">Recent activity</h2>
          <p className="muted small cohort-event-log-lede">
            {isLiveWsConfigured()
              ? "Joins, lesson releases, and submissions update in real time while this page is open."
              : "Joins, lesson releases, submissions, and other cohort events."}
          </p>
        </div>
        <div className="teacher-panel-toolbar">
          {liveLabel ? (
            <span
              className={`cohort-event-log-live cohort-event-log-live--${liveStatus}`}
              aria-live="polite"
            >
              {liveLabel}
            </span>
          ) : null}
          <button
            type="button"
            className="btn secondary tight"
            disabled={loading || exporting || !bearer}
            onClick={() => void handleExport()}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button
            type="button"
            className="btn secondary tight"
            disabled={loading || exporting || !bearer}
            onClick={() => void load({ append: false, nextOffset: 0 })}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="cohort-event-log-filters">
        {lockCohortFilter ? null : (
          <label className="field cohort-event-log-filter">
            <span className="field-label">Cohort</span>
            <select
              className="input"
              value={filterCohortId}
              onChange={(ev) => setFilterCohortId(ev.target.value)}
              disabled={loading}
            >
              <option value="">All cohorts</option>
              {sortedCohorts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                  {c.status === "archived" ? " (concluded)" : ""}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="field cohort-event-log-filter">
          <span className="field-label">Event type</span>
          <select
            className="input"
            value={filterEventType}
            onChange={(ev) => setFilterEventType(ev.target.value)}
            disabled={loading && events.length === 0}
          >
            <option value="">All types</option>
            {EVENT_TYPE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field cohort-event-log-filter">
          <span className="field-label">Student</span>
          <select
            className="input"
            value={filterStudentUserId}
            onChange={(ev) => setFilterStudentUserId(ev.target.value)}
            disabled={studentOptions.length === 0}
          >
            <option value="">All students</option>
            {studentOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field cohort-event-log-filter">
          <span className="field-label">Lesson</span>
          <select
            className="input"
            value={filterLessonId}
            onChange={(ev) => setFilterLessonId(ev.target.value)}
            disabled={lessonOptions.length === 0}
          >
            <option value="">All lessons</option>
            {lessonOptions.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
        </label>

        {hasClientFilters ? (
          <button type="button" className="btn ghost tight cohort-event-log-clear" onClick={clearClientFilters}>
            Clear filters
          </button>
        ) : null}
      </div>

      {hasClientFilters && events.length > 0 ? (
        <p className="muted small cohort-event-log-summary">
          Showing {displayedEvents.length} of {events.length} loaded events
          {hasMore ? " (load more to widen the filter lists)" : null}
        </p>
      ) : null}

      {error ? (
        <p className="warn-banner cohort-event-log-error" role="alert">
          {error}
        </p>
      ) : null}

      {loading && events.length === 0 ? (
        <p className="muted teacher-pad">Loading activity…</p>
      ) : events.length === 0 ? (
        <p className="muted teacher-pad">No activity recorded yet.</p>
      ) : displayedEvents.length === 0 ? (
        <p className="muted teacher-pad">No activity matches these filters.</p>
      ) : (
        <ol className="cohort-event-log-list">
          {displayedEvents.map((event) => {
            const when = parseApiInstant(event.occurredAt);
            return (
              <li key={event.id} className="cohort-event-log-item">
                <img
                  src={cohortEventIconSrc(event)}
                  alt=""
                  className="cohort-event-log-icon"
                  width={40}
                  height={40}
                  decoding="async"
                />
                <div className="cohort-event-log-body">
                  <p className="cohort-event-log-message">
                    {formatCohortEventMessage(event, showCohortInMessage)}
                  </p>
                  <p className="muted small cohort-event-log-meta">
                    {when ? when.toLocaleString() : event.occurredAt}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {hasMore ? (
        <div className="cohort-event-log-more">
          <button
            type="button"
            className="btn ghost"
            disabled={loadingMore || loading}
            onClick={() => void load({ append: true, nextOffset: offset })}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
